import { useState, type CSSProperties } from "react";
import { cn } from "../lib/utils";

export interface OverlayCharacter {
  char: string;
  bbox: { x: number; y: number; w: number; h: number };
  /** Used to color-code the box. Optional — defaults to "warn". */
  score?: number;
  recordId?: string;
}

export interface ImageWithOverlayProps {
  imageUrl: string;
  /** Image natural size in pixels. Used to scale SVG bbox coords to whatever
   *  the rendered <img> width ends up being. */
  imageWidth: number;
  imageHeight: number;
  characters: OverlayCharacter[];
  className?: string;
  /** When true, SVG box clicks navigate via the top-level onSelect. */
  interactive?: boolean;
  /** Called when the user clicks a bbox that has a recordId. */
  onSelect?: (recordId: string) => void;
}

/** Compute a fill/stroke color from a 0-100 score. */
function scoreColor(score: number | undefined): string {
  const v = Math.round(score ?? 75);
  if (v < 60) return "var(--accent)"; // red
  if (v < 75) return "var(--warn)"; // orange
  return "var(--good)"; // green
}

/** Render a fixed-size image + an absolutely-positioned <svg> overlay whose
 *  coords are scaled to the rendered <img> width. */
export function ImageWithOverlay({
  imageUrl,
  imageWidth,
  imageHeight,
  characters,
  className,
  interactive = false,
  onSelect,
}: ImageWithOverlayProps) {
  const [hoverId, setHoverId] = useState<string | null>(null);
  // Always render at a fixed CSS width; SVG scales to match.
  const RENDER_W = 720;
  const scale = RENDER_W / Math.max(1, imageWidth);
  const renderH = Math.round(imageHeight * scale);

  const wrapperStyle: CSSProperties = {
    width: RENDER_W,
    height: renderH,
    maxWidth: "100%",
  };

  return (
    <div
      className={cn(
        "relative mx-auto overflow-hidden rounded-[var(--radius-card)] border border-[color:var(--border)] bg-[color:var(--surface-2)]",
        className,
      )}
      style={wrapperStyle}
    >
      <img
        src={imageUrl}
        alt="练习原图"
        className="absolute inset-0 h-full w-full object-contain"
        draggable={false}
      />
      <svg
        viewBox={`0 0 ${imageWidth} ${imageHeight}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 h-full w-full"
      >
        {characters.map((ch, idx) => {
          const id = ch.recordId ?? `${ch.char}-${idx}`;
          const color = scoreColor(ch.score);
          const hovered = hoverId === id;
          return (
            <g
              key={id}
              onMouseEnter={() => setHoverId(id)}
              onMouseLeave={() => setHoverId(null)}
              onClick={() => {
                if (!interactive || !ch.recordId || !onSelect) return;
                onSelect(ch.recordId);
              }}
              style={{ cursor: interactive && ch.recordId ? "pointer" : "default" }}
            >
              <rect
                x={ch.bbox.x}
                y={ch.bbox.y}
                width={ch.bbox.w}
                height={ch.bbox.h}
                fill={color}
                fillOpacity={hovered ? 0.18 : 0.08}
                stroke={color}
                strokeOpacity={hovered ? 1 : 0.7}
                strokeWidth={hovered ? Math.max(2, ch.bbox.w * 0.02) : Math.max(1, ch.bbox.w * 0.012)}
                rx={4}
              />
              {hovered && (
                <g>
                  <rect
                    x={ch.bbox.x}
                    y={Math.max(0, ch.bbox.y - ch.bbox.h * 0.35)}
                    width={Math.max(ch.bbox.w * 0.5, 60)}
                    height={ch.bbox.h * 0.3}
                    fill={color}
                    rx={4}
                  />
                  <text
                    x={ch.bbox.x + 6}
                    y={Math.max(ch.bbox.h * 0.3, ch.bbox.y - ch.bbox.h * 0.05)}
                    fontSize={Math.max(20, ch.bbox.h * 0.25)}
                    fill="white"
                    fontFamily="serif"
                    dominantBaseline="middle"
                  >
                    {ch.char}
                    {ch.score !== undefined ? ` ${Math.round(ch.score)}` : ""}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}